import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { PiArrowCounterClockwiseBold, PiCheckCircleFill, PiProhibitBold, PiUserMinusBold, PiWarningBold } from 'react-icons/pi';
import { Button } from '@v2/design-system';
import V2Modal from '@v2/components/ui/V2Modal';
import api from '@v2/lib/api';

export type DeactivationInfo = {
    id: number;
    name: string;
    is_active?: boolean;
    deactivated_at?: string | null;
    deactivation_reason?: string | null;
    deactivated_by?: string | null;
};

type Pending = { key: string; label: string; count: number; hint: string };

const errorMessage = (err: unknown, fallback: string) =>
    (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? fallback;

/** Faixa no topo do usuário desativado: quando, por quem e por quê. */
export function DeactivatedBanner({ user }: { user: DeactivationInfo }) {
    if (user.is_active !== false) return null;
    return (
        <div className="mb-4 flex items-start gap-3 rounded-xl border border-gray-300 bg-gray-50 px-4 py-3 text-sm text-gray-700 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200">
            <PiProhibitBold className="mt-0.5 size-5 shrink-0 text-gray-500" />
            <div>
                <p className="font-semibold">Usuário desativado{user.deactivated_at ? ` em ${user.deactivated_at}` : ''}{user.deactivated_by ? ` por ${user.deactivated_by}` : ''}.</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                    Não entra no Hub, no painel nem no app. O histórico (vendas, comissões, reservas) continua.
                    {user.deactivation_reason ? ` Motivo: ${user.deactivation_reason}` : ''}
                </p>
            </div>
        </div>
    );
}

/** Botão de desligamento (com o que ainda está com a pessoa) ou de reativação. */
export function DeactivationButton({ user, compact = false }: { user: DeactivationInfo; compact?: boolean }) {
    const qc = useQueryClient();
    const [open, setOpen] = useState(false);
    const [reason, setReason] = useState('');
    const active = user.is_active !== false;

    const impact = useQuery({
        queryKey: ['users', user.id, 'deactivation-impact'],
        enabled: open && active,
        queryFn: () => api.get<{ data: Pending[] }>(`/users/${user.id}/deactivation-impact`).then((r) => r.data.data),
    });

    const refresh = () => {
        void qc.invalidateQueries({ queryKey: ['users'] });
    };

    const deactivate = useMutation({
        mutationFn: async () => (await api.post<{ message: string }>(`/users/${user.id}/deactivate`, { reason: reason.trim() })).data,
        onSuccess: (res) => {
            toast.success(res.message);
            setOpen(false);
            setReason('');
            refresh();
        },
        onError: (err) => toast.error(errorMessage(err, 'Não foi possível desativar.')),
    });

    const reactivate = useMutation({
        mutationFn: async () => (await api.post<{ message: string }>(`/users/${user.id}/reactivate`)).data,
        onSuccess: (res) => {
            toast.success(res.message);
            refresh();
        },
        onError: (err) => toast.error(errorMessage(err, 'Não foi possível reativar.')),
    });

    if (!active) {
        return (
            <button
                type="button"
                title={compact ? 'Reativar usuário' : undefined}
                aria-label="Reativar usuário"
                onClick={(e) => {
                    e.stopPropagation();
                    if (window.confirm(`Reativar ${user.name}? A pessoa volta a poder entrar no Hub.`)) reactivate.mutate();
                }}
                disabled={reactivate.isPending}
                className={
                    compact
                        ? 'rounded p-1 text-emerald-600 hover:bg-emerald-50 disabled:opacity-50 dark:hover:bg-emerald-500/10'
                        : 'inline-flex items-center gap-1.5 rounded-lg border border-emerald-200 bg-white px-3 py-2 text-xs font-medium text-emerald-700 hover:bg-emerald-50 disabled:opacity-50 dark:border-emerald-500/40 dark:bg-gray-800 dark:text-emerald-300'
                }
            >
                <PiArrowCounterClockwiseBold className="size-4" />
                {!compact && (reactivate.isPending ? 'Reativando…' : 'Reativar usuário')}
            </button>
        );
    }

    const pending = impact.data ?? [];

    return (
        <>
            <button
                type="button"
                title={compact ? 'Desativar usuário' : undefined}
                aria-label="Desativar usuário"
                onClick={(e) => {
                    e.stopPropagation();
                    setOpen(true);
                }}
                className={
                    compact
                        ? 'rounded p-1 text-gray-400 hover:bg-rose-50 hover:text-rose-600 dark:hover:bg-rose-500/10'
                        : 'inline-flex items-center gap-1.5 rounded-lg border border-rose-200 bg-white px-3 py-2 text-xs font-medium text-rose-700 hover:bg-rose-50 dark:border-rose-500/40 dark:bg-gray-800 dark:text-rose-300'
                }
            >
                <PiUserMinusBold className="size-4" />
                {!compact && 'Desativar usuário'}
            </button>
            <V2Modal
                open={open}
                onClose={() => setOpen(false)}
                title={`Desativar ${user.name}`}
                description="Para desligamento de funcionário. Nada é apagado e dá para reativar depois."
                size="lg"
                footer={
                    <>
                        <Button variant="outline" onClick={() => setOpen(false)}>
                            Cancelar
                        </Button>
                        <Button
                            className="bg-rose-600 hover:bg-rose-700"
                            disabled={reason.trim().length < 3}
                            isLoading={deactivate.isPending}
                            onClick={() => deactivate.mutate()}
                        >
                            Desativar
                        </Button>
                    </>
                }
            >
                <div className="space-y-4 text-sm">
                    <div>
                        <p className="mb-1.5 text-xs font-semibold text-gray-700 dark:text-gray-200">Ao desativar</p>
                        <ul className="space-y-1 text-xs text-gray-600 dark:text-gray-300">
                            {[
                                'Não entra mais no Hub, no painel nem no app (as sessões abertas são encerradas na hora).',
                                'Sai das listas de quem recebe avisos e de aprovador de comissão.',
                                'Vendas, comissões, reservas e o que a pessoa fez continuam no histórico.',
                            ].map((t) => (
                                <li key={t} className="flex gap-1.5">
                                    <PiCheckCircleFill className="mt-0.5 size-3.5 shrink-0 text-gray-400" /> {t}
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div>
                        <p className="mb-1.5 text-xs font-semibold text-gray-700 dark:text-gray-200">Ainda está com a pessoa</p>
                        {impact.isLoading ? (
                            <p className="text-xs text-gray-400">Conferindo…</p>
                        ) : pending.length === 0 ? (
                            <p className="flex items-center gap-1.5 rounded-lg bg-emerald-50 px-3 py-2 text-xs text-emerald-800 dark:bg-emerald-500/10 dark:text-emerald-200">
                                <PiCheckCircleFill className="size-4" /> Nada pendente.
                            </p>
                        ) : (
                            <ul className="space-y-1.5">
                                {pending.map((p) => (
                                    <li key={p.key} className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-100">
                                        <p className="flex items-center gap-1.5 font-semibold">
                                            <PiWarningBold className="size-3.5" /> {p.count} · {p.label}
                                        </p>
                                        <p className="mt-0.5 opacity-80">{p.hint}</p>
                                    </li>
                                ))}
                            </ul>
                        )}
                        {pending.length > 0 && <p className="mt-1.5 text-[11px] text-gray-400">Dá para desativar agora e resolver depois — mas o ideal é passar para alguém antes.</p>}
                    </div>

                    <label className="block">
                        <span className="mb-1 block text-xs font-semibold text-gray-700 dark:text-gray-200">
                            Motivo <span className="text-rose-500">*</span>
                        </span>
                        <textarea
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            rows={2}
                            placeholder="Ex.: desligamento em 22/10/2026"
                            className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900"
                        />
                    </label>
                </div>
            </V2Modal>
        </>
    );
}
